import { FC, useCallback, useEffect, useState } from 'react';
import { SearchIndex } from 'algoliasearch';
import algoliasearch, { SearchClient } from 'algoliasearch';
import { SearchAuthContext } from 'context/Search/AuthContext';
import { SearchAuthProviderProps, SearchConfig } from 'types';
import { getSearchAuth } from 'api/migration';

const SEARCH_CONFIG_KEY = 'search_config';
const SEARCH_CONFIG_VERSION_KEY = 'search_config_version';
const SEARCH_CONFIG_VERSION = 'tenant_algolia_auth_v1';

export const SearchAuthProvider: FC<SearchAuthProviderProps> = ({ children }) => {
  const [client, setClient] = useState<SearchClient | null>(null);
  const [index, setIndex] = useState<SearchIndex | null>(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const fetchAuthFromAPI = useCallback(async (): Promise<SearchConfig | null> => {
    try {
      const data = await getSearchAuth();
      const config: SearchConfig = {
        api_key: data.api_key,
        app_id: data.app_id,
        index: data.index,
        expiry_time: data.expiry_time,
      };
      localStorage.setItem(SEARCH_CONFIG_KEY, JSON.stringify(config));
      localStorage.setItem(SEARCH_CONFIG_VERSION_KEY, SEARCH_CONFIG_VERSION);
      const clientData = algoliasearch(config.app_id, config.api_key);
      const indexData = clientData.initIndex(config.index);
      setClient(clientData);
      setIndex(indexData as SearchIndex);
      return config;
    } catch (error) {
      console.error('Error fetching auth token:', error);
      return null;
    }
  }, []);

  const isWithinOneMinute = (epochTime: number): boolean => {
    const currentTime = Math.round(Date.now() / 1000);

    return epochTime - currentTime < 6000;
  };

  const loadAuthFromLocalStorage = useCallback((): SearchConfig | null => {
    const configVersion = localStorage.getItem(SEARCH_CONFIG_VERSION_KEY);
    if (configVersion !== SEARCH_CONFIG_VERSION) {
      localStorage.removeItem(SEARCH_CONFIG_KEY);
      localStorage.setItem(SEARCH_CONFIG_VERSION_KEY, SEARCH_CONFIG_VERSION);
      return null;
    }

    const storedConfig = localStorage.getItem(SEARCH_CONFIG_KEY);
    if (storedConfig) {
      const config: SearchConfig = JSON.parse(storedConfig);
      if (!isWithinOneMinute(config.expiry_time)) {
        const clientData = algoliasearch(config.app_id, config.api_key);
        const indexData = clientData.initIndex(config.index);
        setClient(clientData);
        setIndex(indexData as SearchIndex);
        return config;
      }
    }
    return null;
  }, []);

  const setApiToken = useCallback(async () => {
    let config = loadAuthFromLocalStorage();
    if (!config) {
      config = await fetchAuthFromAPI();
    }
  }, [fetchAuthFromAPI, loadAuthFromLocalStorage]);

  useEffect(() => {
    setApiToken();
    const intervalId = setInterval(
      () => {
        setApiToken();
      },
      60 * 60 * 1000,
    );
    return () => clearInterval(intervalId);
  }, [setApiToken, refetchCount]);

  return <SearchAuthContext.Provider value={{ client, index, setRefetchCount }}>{children}</SearchAuthContext.Provider>;
};

export default SearchAuthProvider;
